/**
 * The drawn houses the hubs fall back to when there is no photograph.
 *
 * Both stages want the same ten drawings for different reasons — a home has a
 * type to match against, a search group has only its id — so the artwork, the
 * glob and the stable-pick hash live here and the per-stage mapping lives with
 * the stage. `screens/home/home-illustrations.ts` layers the home-type map on
 * top; `screens/home-search/group-illustrations.ts` needs nothing but the seed.
 *
 * ── Assets ───────────────────────────────────────────────────────────────────
 * Drop the artwork into `src/assets/illustrations/homes/` as
 * `<slug>.webp|png|jpg` using the slugs below. The glob resolves at build time,
 * so files appear without a code change here, and until they do the lookups
 * return undefined and callers render without artwork.
 */

/** The design's ten home-type drawings. */
export const ILLUSTRATION_SLUGS = [
    'suburban-family-home',
    'city-townhouse',
    'modern-condo',
    'urban-apartment',
    'coastal-cottage',
    'mountain-cabin',
    'garden-duplex',
    'lake-house',
    'country-farmhouse',
    'desert-bungalow',
] as const;

export type IllustrationSlug = (typeof ILLUSTRATION_SLUGS)[number];

// Vite resolves this at build time: each match is hashed, copied and served like
// a static import. An empty directory yields an empty record rather than an
// error, which is what lets the hubs ship before the artwork does.
// Relative rather than aliased: `import.meta.glob` patterns are resolved by the
// bundler before aliases are applied, so `@/` is rejected outright.
const modules = import.meta.glob<{ default: string }>(
    '../assets/illustrations/homes/*.{webp,png,jpg,jpeg}',
    { eager: true },
);

const URL_BY_SLUG: Partial<Record<IllustrationSlug, string>> = Object.fromEntries(
    Object.entries(modules).map(([path, module]) => [
        path.replace(/^.*\/(.+)\.\w+$/, '$1'),
        module.default,
    ]),
);

/** FNV-1a. Small, stable, and not seeded by anything that changes per session. */
function hash(value: string): number {
    let h = 0x811c9dc5;

    for (let i = 0; i < value.length; i += 1) {
        h ^= value.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }

    return h >>> 0;
}

/**
 * Which drawing stands in for a record.
 *
 * `preferred` wins when the caller has something better than a guess — a home's
 * type. Otherwise the pick is keyed off the seed rather than an array index or
 * `Math.random`, so a record's picture is the same on every render, in every
 * session, and does not change when the list is reordered. A picture that moved
 * would read as a bug even though nothing broke.
 */
export function illustrationSlugForSeed(
    seed: string,
    preferred?: IllustrationSlug,
    pool: readonly IllustrationSlug[] = ILLUSTRATION_SLUGS,
): IllustrationSlug {
    return preferred ?? pool[hash(seed) % pool.length];
}

/** The URL for a slug, or undefined when that asset is not present. */
export function illustrationUrl(slug: IllustrationSlug): string | undefined {
    return URL_BY_SLUG[slug];
}

/**
 * The image for a record, or undefined when there is nothing to show.
 * Callers must handle undefined — see the module note on assets.
 */
export function illustrationForSeed(
    seed: string,
    preferred?: IllustrationSlug,
    pool?: readonly IllustrationSlug[],
): string | undefined {
    return illustrationUrl(illustrationSlugForSeed(seed, preferred, pool));
}
